import i18n from './index';
import NumberFormat from 'react-number-format';
import React from 'react';
import { TFunction } from 'i18next';

const AddComma = (num: number) => {
	var regexp = /\B(?=(\d{3})+(?!\d))/g;
	return num.toString().replace(regexp, ',');
};

export function formatMoney(krw: number) {
	const isEnglish = (i18n.language || '').toLowerCase().startsWith('en');
	const amount = krw || 0;
	const displayStock = isEnglish ? Math.round(amount / 10) : amount;
	const formattedStock = AddComma(displayStock);
	let stockPrefix = isEnglish ? '$' : '';
	let stockSuffix = isEnglish ? '' : '원';
	return <NumberFormat style={{ margin: 'auto 0' }} value={formattedStock} displayType={'text'} thousandSeparator={true} suffix={stockSuffix} prefix={stockPrefix} />;
}

export function formatMoneyAmount(krw: number) {
	const isEnglish = (i18n.language || '').toLowerCase().startsWith('en');
	const displayStock = isEnglish ? Math.round(krw / 10) : krw;
	let stockPrefix = isEnglish ? '$' : '';
	let stockSuffix = isEnglish ? '' : '원';
	const formattedStock = AddComma(displayStock);
	return stockPrefix + formattedStock.toString() + stockSuffix;
}

export function formatToTimezone(utcTimeString: string): [string, string, string] | null {
	if (!utcTimeString || utcTimeString.length < 19) return null;

	let isoString = utcTimeString;
	if (!isoString.endsWith('Z')) {
		isoString = isoString.replace(' ', 'T') + 'Z';
	}
	const date = new Date(isoString);
	//console.log('date', date);

	if (isNaN(date.getTime())) return null;

	const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	const fullDateFormatter = new Intl.DateTimeFormat('sv-SE', {
		timeZone: userTimeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});

	const dateFormatter = new Intl.DateTimeFormat('en-US', {
		timeZone: userTimeZone,
		month: '2-digit',
		day: '2-digit',
	});

	const timeFormatter = new Intl.DateTimeFormat('en-US', {
		timeZone: userTimeZone,
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});

	return [
		dateFormatter.format(date), // 예: "05/20"
		timeFormatter.format(date), // 예: "12:00"
		fullDateFormatter.format(date), // 예: "2026/05/20"
	];
}

export const getTranslatedLicenseName = (t: TFunction, licenseName?: string) => {
	if (!licenseName) return '';
	const key = LICENSE_NAME_TO_I18N_KEY[licenseName];
	return key ? t(key) : licenseName;
};

export const getTranslatedMaterialName = (t: TFunction, materialCode: string, materialName?: string) => {
	if (!materialCode) return '';
	//const versionKey = version >= 4 ? version - 2 : version;
	const materialIndex = materialName ? Number(materialName.match(/^(\d+)\./)?.[1]) : null;
	const key = MATERIAL_CODE_TO_I18N_KEY[materialCode];
	return key ? (materialIndex ? materialIndex + '. ' : '') + t(key) : materialCode;
};

const LICENSE_NAME_TO_I18N_KEY: Record<string, string> = {
	텔레핸들러: 'LicenseNames.telehandler',
	버기카: 'LicenseNames.buggyCar',
	쌍동선: 'LicenseNames.twinShip',
	제설트럭: 'LicenseNames.snowplowTruck',
	에어보트: 'LicenseNames.airboat',
	덤프트럭: 'LicenseNames.dumpTruck',
	수상비행기: 'LicenseNames.seaplane',
	레이싱카: 'LicenseNames.racingCar',
	'백호우 로더': 'LicenseNames.backhoeLoader',
};

const MATERIAL_CODE_TO_I18N_KEY: Record<string, string> = {
	'0241226': 'MaterialNames.0241226',
	'0242023': 'MaterialNames.0242023',
	'0300401': 'MaterialNames.0300401',
	'0302023': 'MaterialNames.0302023',
	'0302024': 'MaterialNames.0302024',
	'0302026': 'MaterialNames.0302026',
	'0302101': 'MaterialNames.0302101',
	'0302123': 'MaterialNames.0302123',
	'0302126': 'MaterialNames.0302126',
	'0302201': 'MaterialNames.0302201',
	'0302221': 'MaterialNames.0302221',
	'0302226': 'MaterialNames.0302226',
	'0302301': 'MaterialNames.0302301',
	'0302321': 'MaterialNames.0302321',
	'0302326': 'MaterialNames.0302326',
	'0302423': 'MaterialNames.0302423',
	'0306924': 'MaterialNames.0306924',
	'0307026': 'MaterialNames.0307026',
	'0362321': 'MaterialNames.0362321',
	'0366626': 'MaterialNames.0366626',
	'0371024': 'MaterialNames.0371024',
	'0371026': 'MaterialNames.0371026',
	'0379501': 'MaterialNames.0379501',
	'0379526': 'MaterialNames.0379526',
	'0383926': 'MaterialNames.0383926',
	'0407024': 'MaterialNames.0407024',
	'0460026': 'MaterialNames.0460026',
	'0486526': 'MaterialNames.0486526',
	'3005741': 'MaterialNames.3005741',
	'3005748': 'MaterialNames.3005748',
	'4106347': 'MaterialNames.4106347',
	'4121741': 'MaterialNames.4121741',
	'4158355': 'MaterialNames.4158355',
	'4158848': 'MaterialNames.4158848',
	'4159007': 'MaterialNames.4159007',
	'4177932': 'MaterialNames.4177932',
	'4179874': 'MaterialNames.4179874',
	'4179875': 'MaterialNames.4179875',
	'4211002': 'MaterialNames.4211002',
	'4211046': 'MaterialNames.4211046',
	'4211063': 'MaterialNames.4211063',
	'4211350': 'MaterialNames.4211350',
	'4211412': 'MaterialNames.4211412',
	'4211525': 'MaterialNames.4211525',
	'4211569': 'MaterialNames.4211569',
	'4211804': 'MaterialNames.4211804',
	'4222017': 'MaterialNames.4222017',
	'4222960': 'MaterialNames.4222960',
	'4226179': 'MaterialNames.4226179',
	'4244368': 'MaterialNames.4244368',
	'4246901': 'MaterialNames.4246901',
	'4282744': 'MaterialNames.4282744',
	'4282786': 'MaterialNames.4282786',
	'4282789': 'MaterialNames.4282789',
	'4288960': 'MaterialNames.4288960',
	'4289542': 'MaterialNames.4289542',
	'4500469': 'MaterialNames.4500469',
	'4515367': 'MaterialNames.4515367',
	'4535739': 'MaterialNames.4535739',
	'4558953': 'MaterialNames.4558953',
	'4569733': 'MaterialNames.4569733',
	'4598526': 'MaterialNames.4598526',
	'4599984': 'MaterialNames.4599984',
	'4600177': 'MaterialNames.4600177',
	'4611703': 'MaterialNames.4611703',
	'4617848': 'MaterialNames.4617848',
	'4620693': 'MaterialNames.4620693',
	'4631877': 'MaterialNames.4631877',
	'6000606': 'MaterialNames.6000606',
	'6006739': 'MaterialNames.6006739',
	'6015891': 'MaterialNames.6015891',
	'6020193': 'MaterialNames.6020193',
	'6028812': 'MaterialNames.6028812',
	'6029947': 'MaterialNames.6029947',
	'6037412': 'MaterialNames.6037412',
	'6037750': 'MaterialNames.6037750',
	'6039869': 'MaterialNames.6039869',
	'6047220': 'MaterialNames.6047220',
	'6055065': 'MaterialNames.6055065',
	'6055069': 'MaterialNames.6055069',
	'6096304': 'MaterialNames.6096304',
	'6099483': 'MaterialNames.6099483',
};
